import { Tooltip as RadixTooltip } from "radix-ui";
import { type PropsWithChildren,useState } from "react";

import { cn } from "$/lib/utils/functions/misc.functions";

type Props = {
  content: string | JSX.Element;
  side?: "top" | "right" | "bottom" | "left";
  delayDuration?: number;
  skipDelayDuration?: number;
  contentClassName?: string;
  // Force l'ouverture depuis un déclencheur externe (ex. survol d'une
  // barre de graphique), en plus du hover/tap natif sur le trigger.
  forceOpen?: boolean;
};

export default function Tooltip({
  content,
  side = "bottom",
  delayDuration,
  skipDelayDuration,
  children,
  contentClassName,
  forceOpen,
}: PropsWithChildren<Props>) {
  const [open, setOpen] = useState(false);
  const isOpen = open || !!forceOpen;

  return (
    <RadixTooltip.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
    >
      <RadixTooltip.Root open={isOpen} onOpenChange={setOpen}>
        <RadixTooltip.Trigger
          asChild
          onPointerDown={(event) => {
            // Prevent Radix's built-in "close if already open" handler,
            // which would otherwise fight our manual toggle below.
            if (window.matchMedia("(hover: none)").matches) {
              event.preventDefault();
            }
          }}
          onClick={(event) => {
            // Radix's tooltip only opens on hover/focus, which doesn't exist
            // on touch devices. On those, toggle it manually via tap, and
            // prevent Radix's built-in onClick (which always closes it).
            if (window.matchMedia("(hover: none)").matches) {
              event.preventDefault();
              setOpen((prev) => !prev);
            }
          }}
        >
          {children}
        </RadixTooltip.Trigger>

        <RadixTooltip.Portal>
          <RadixTooltip.Content
            align="center"
            side={side}
            sideOffset={6}
            className={cn(
              "shadow-box data-[state=closed]:animate-fade-out data-[state=delayed-open]:data-[side=top]:animate-fade-in z-[9999] w-56 gap-2 rounded-xl bg-[#0E0549] bg-opacity-90 p-2 text-xs font-medium text-[#FFFDFC] shadow-[3px_3px_10px_rgba(0,0,0,0.25)] backdrop-blur-sm",
              contentClassName,
            )}
          >
            {content}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
